import React, { useEffect, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { FaRupeeSign } from "react-icons/fa";
import * as Yup from "yup";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import "./receiptModule.css";
import Navbar from "../navbar/navbar";
import { getAllEkadasiList, getGotraMasterList, getNakshatraMasterList } from "../../services/admin.service";
import {
    formatISODate,
    generateReceipt,
    getBase64ImageFromUrl,
    getDevoteeMasterData,
} from "../../services/receipt.service";
import KanikaReceiptPrint from "../../print/kanikaReceiptPrint";
import headerLogo from "../../assets/images/DailyPoojaReport.png";
import PaymentModalComponent from "./paymentModalComponent";
import { openRazorpayCheckout } from "./razorpayCheckout";
import {
    toNumber,
    getMinDate,
    getMaxDate,
    hasPaymentDetails,
    loadMasterList,
    buildPaymentDto,
    commonDevoteeSchema,
    MobileEntryCard,
    PaymentLoader,
    DevoteeDetailsSection,
    PaymentDetailsPreview,
    PaymentTypeSelect,
    SubmitBar,
    getDefaultGotra,
    getDefaultNakshatra,
} from "./receiptShared";

const KanikaReceiptComponent = () => {
    const roleName = localStorage.getItem("roleName");
    const username = localStorage.getItem("username");
    const isUser = roleName === "ROLE_USER";
    const isAdmin = roleName === "ROLE_ADMIN";
    const receiptsRoute = isUser ? "/user-receipts" : "/print-receipts";
    const navigate = useNavigate();

    const [mobileEntered, setMobileEntered] = useState(false);
    const [mobileNumber, setMobileNumber] = useState("");
    const [gotraMasterList, setGotraMasterList] = useState([]);
    const [nakshatraMasterList, setNakshatraMasterList] = useState([]);
    const [ekadasiMasterList, setEkadasiMasterList] = useState([]);
    const [devoteeData, setDevoteeData] = useState(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    /* ------------------------- data loading ------------------------- */

    useEffect(() => {
        const fetchData = async () => {
            if (isUser && username) {
                setMobileNumber(username);
                setMobileEntered(true);
                try {
                    const result = await getDevoteeMasterData(username);
                    setDevoteeData(result || {});
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        loadMasterList(getGotraMasterList, setGotraMasterList, "Gotra");
        loadMasterList(getNakshatraMasterList, setNakshatraMasterList, "Nakshatra");
        loadMasterList(getAllEkadasiList, setEkadasiMasterList, "ekadasi");
    }, []);

    const handleMobileSubmit = async () => {
        if (mobileNumber.length !== 10) {
            Swal.fire("Error", "Please enter a valid 10-digit mobile number", "error");
            return;
        }
        setLoading(true);
        try {
            let result = null;
            try {
                result = await getDevoteeMasterData(mobileNumber);
            } catch (err) {
                console.warn("Devotee not found or error fetching devotee details:", err);
                result = null;
            }
            setDevoteeData(result || {});
            setMobileEntered(true);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Could not load devotee details. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const isDevoteeNameDisabled = Boolean(devoteeData?.nameOfDevotee && devoteeData.nameOfDevotee.trim());

    /* ------------------------- form config ------------------------- */

    const initialValues = {
        devoteeName: devoteeData?.nameOfDevotee || "",
        mobile: devoteeData?.mobileNo || mobileNumber,
        alternateMobile: devoteeData?.alternateNo || "",
        nakshatra: getDefaultNakshatra(devoteeData, nakshatraMasterList),
        gotra: getDefaultGotra(devoteeData, gotraMasterList),
        donationAmount: "",
        donationFor: "",
        poojaType1: "99",
        poojaDateTime1: null,
        paymentType: isUser ? "Online" : "",
        paymentDetails: {},
    };

    const validationSchema = Yup.object({
        ...commonDevoteeSchema,
        poojaDateTime1: Yup.date().typeError("Seva Date is required").required("Seva Date is required"),
        donationAmount: Yup.number()
            .typeError("Must be a number")
            .required("Amount is required")
            .moreThan(0, "Amount must be greater than 0"),
        donationFor: Yup.string()
            .required("Donation for is required")
            .max(100, "Must be 100 characters or less")
            .trim()
            .test("not-empty", "Donation for must not be empty", (value) => value && value.trim().length > 0),
    });

    const nakshatraOptions = nakshatraMasterList.map((item) => ({
        value: item.nakshatraId,
        label: item.nakshatraName,
    }));

    const gotraOptions = gotraMasterList.map((item) => ({
        value: item.gotraId,
        label: item.gotraName,
    }));

    /* ------------------------- handlers ------------------------- */

    const handleDateChange = (date, fieldName, setFieldValue) => {
        if (!date) {
            setFieldValue(fieldName, null);
            return;
        }
        const selectedDateStr = date.toLocaleDateString("en-CA");
        const ekadasi = ekadasiMasterList.find((item) => item.ekadasiDate === selectedDateStr);
        if (ekadasi) {
            Swal.fire({
                icon: "warning",
                title: "Ekadasi Alert!",
                text: `${ekadasi.ekadasiRemarks} – No Seva allowed.`,
            });
            setFieldValue(fieldName, null);
        } else {
            setFieldValue(fieldName, formatISODate(date));
        }
    };

    const handlePaymentTypeChange = (e, setFieldValue) => {
        const value = e.target.value;
        setFieldValue("paymentType", value);
        setSelectedPaymentType(value);
        if (["CQ", "RT"].includes(value)) {
            setShowModal(true);
        } else {
            setFieldValue("paymentDetails", {});
        }
    };

    const handleCancel = (resetForm) => {
        if (resetForm) resetForm();
        setMobileEntered(false);
        setMobileNumber("");
        setDevoteeData(null);
        setSelectedPaymentType("");
    };

    const finishAndLeave = (resetForm) => {
        resetForm();
        setMobileEntered(false);
        setMobileNumber("");
        setDevoteeData(null);
        navigate(receiptsRoute);
    };

    const handleSubmit = async (values, { resetForm }) => {
        if (!values.paymentType) {
            Swal.fire("Payment Type Required", "Please select a payment type.", "warning");
            return;
        }

        // ---- Cash / Cheque / RTGS ----
        if (["CA", "CQ", "RT"].includes(values.paymentType)) {
            const requiresDetails = ["CQ", "RT"].includes(values.paymentType);
            if (requiresDetails && !hasPaymentDetails(values.paymentDetails)) {
                Swal.fire("Payment Details Required", "Please enter the cheque/RTGS payment details.", "warning");
                return;
            }

            const confirmResult = await Swal.fire({
                title: "Generate Receipt?",
                text: "Are you sure you want to generate this Kanika receipt?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Generate",
                cancelButtonText: "Cancel",
            });
            if (!confirmResult.isConfirmed) return;

            setLoading(true);
            try {
                const response = await generateReceipt(values);
                if (!response) {
                    Swal.fire("Error", "Failed to generate receipt. Please try again.", "error");
                    return;
                }
                const base64Logo = await getBase64ImageFromUrl(headerLogo);
                await KanikaReceiptPrint(response, base64Logo, nakshatraMasterList, gotraMasterList);
                finishAndLeave(resetForm);
            } catch (error) {
                console.error("Error generating receipt:", error);
                Swal.fire("Error", "Error generating receipt. Please try again.", "error");
            } finally {
                setLoading(false);
            }
            return;
        }

        // ---- Online (Razorpay) ----
        if (values.paymentType === "Online") {
            const donationAmt = toNumber(values.donationAmount);
            if (!donationAmt || donationAmt <= 0) {
                Swal.fire("Invalid Amount", "Donation amount must be greater than zero for online payment.", "warning");
                return;
            }

            const onPaymentSuccess = async (paymentData, pdfWindow) => {
                setLoading(true);
                try {
                    const receiptData = { ...values, paymentDetails: buildPaymentDto(paymentData) };
                    const response = await generateReceipt(receiptData);
                    if (!response) {
                        if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                        Swal.fire("Error", "Failed to generate receipt. Please contact temple office.", "error");
                        return;
                    }
                    const base64Logo = await getBase64ImageFromUrl(headerLogo);
                    await KanikaReceiptPrint(response, base64Logo, nakshatraMasterList, gotraMasterList, pdfWindow);
                    finishAndLeave(resetForm);
                } catch (error) {
                    console.error("Error generating receipt after payment:", error);
                    if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                    Swal.fire("Error", "Payment succeeded, but receipt generation encountered an error. Please contact temple office.", "error");
                } finally {
                    setLoading(false);
                }
            };

            await openRazorpayCheckout(
                donationAmt,
                values,
                setLoading,
                onPaymentSuccess,
                "Kanika Receipt Payment"
            );
            return;
        }

        Swal.fire("Invalid Payment Type", "Please select a valid payment type.", "warning");
    };

    /* ------------------------- render ------------------------- */

    return (
        <div className="card receipt-page">
            <Navbar />
            <div className="receipt-heading">
                <h3 className="custom-heading">Kanika Receipt</h3>
            </div>

            <div className="receipt-content">
                {!mobileEntered ? (
                    <MobileEntryCard
                        mobileNumber={mobileNumber}
                        setMobileNumber={setMobileNumber}
                        onSubmit={handleMobileSubmit}
                    />
                ) : (
                    <>
                        {loading && <PaymentLoader text="Processing, please wait..." />}
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize={true}
                        >
                            {({ setFieldValue, values, resetForm }) => (
                                <>
                                    <Form autoComplete="off">
                                        <div className="card receipt-form-card">
                                            <DevoteeDetailsSection
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                nakshatraOptions={nakshatraOptions}
                                                gotraOptions={gotraOptions}
                                                isNameDisabled={isDevoteeNameDisabled}
                                                showCancel={!isUser}
                                                onCancel={() => handleCancel(resetForm)}
                                            />

                                            <hr className="my-4" />
                                            <h4 className="section-title pooja">Seva Details</h4>
                                            <div className="row g-3 text-start">
                                                <div className="col-md-2">
                                                    <label className="form-label">Amount</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text"><FaRupeeSign /></span>
                                                        <Field name="donationAmount" className="form-control" type="number" />
                                                    </div>
                                                    <ErrorMessage name="donationAmount" component="div" className="text-danger" />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label">Seva Date</label>
                                                    <DatePicker
                                                        id="poojaDateTime1"
                                                        name="poojaDateTime1"
                                                        placeholderText="Select Date"
                                                        selected={values.poojaDateTime1}
                                                        onChange={(date) => handleDateChange(date, "poojaDateTime1", setFieldValue)}
                                                        className="form-control"
                                                        dateFormat="dd-MM-yyyy"
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        minDate={getMinDate()}
                                                        maxDate={getMaxDate()}
                                                        onKeyDown={(event) => event.preventDefault()}
                                                    />
                                                    <ErrorMessage name="poojaDateTime1" component="div" className="text-danger" />
                                                </div>
                                                <div className={isAdmin ? "col-md-6" : "col-md-8"}>
                                                    <label className="form-label">Donation For</label>
                                                    <Field name="donationFor" className="form-control" />
                                                    <ErrorMessage name="donationFor" component="div" className="text-danger" />
                                                </div>
                                                {isAdmin && (
                                                    <div className="col-md-2">
                                                        <PaymentTypeSelect
                                                            onChange={(e) => handlePaymentTypeChange(e, setFieldValue)}
                                                            onEditDetails={() => setShowModal(true)}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                            {hasPaymentDetails(values.paymentDetails) && (
                                                <div className="row mt-4">
                                                    <div className="col-md-6">
                                                        <PaymentDetailsPreview paymentDetails={values.paymentDetails} />
                                                    </div>
                                                </div>
                                            )}

                                            <SubmitBar
                                                paymentType={values.paymentType}
                                                loading={loading}
                                                showCancel={!isUser}
                                                onCancel={() => handleCancel(resetForm)}
                                            />
                                        </div>
                                    </Form>

                                    {showModal && (
                                        <PaymentModalComponent
                                            selectedPaymentType={selectedPaymentType}
                                            onClose={() => setShowModal(false)}
                                            setPaymentDetails={(data) => {
                                                setFieldValue("paymentDetails", data);
                                                setShowModal(false);
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </Formik>
                    </>
                )}
            </div>
        </div>
    );
};

export default KanikaReceiptComponent;
